import { useEffect, useState } from "react";
import { CalendarPlus, Loader2, Save, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import {
  useCreateEvent,
  useUpdateEvent,
  type EventInput,
} from "@/hooks/useEventMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatBytes } from "@/lib/format";
import type { EventRow, EventType } from "@/lib/types";

const TYPE_OPTIONS: { value: EventType; label: string }[] = [
  { value: "conference", label: "Conference" },
  { value: "workshop",   label: "Workshop" },
  { value: "training",   label: "Training" },
  { value: "webinar",    label: "Webinar" },
  { value: "field",      label: "Field visit" },
  { value: "other",      label: "Other" },
];

interface EventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: EventRow | null;
}

/** Create + edit form for Events. Director+ only (gated by API +
 *  by the parent page hiding the buttons for everyone else). */
export function EventFormDialog({ open, onOpenChange, event }: EventFormDialogProps) {
  const isEdit = !!event;
  const { data: user } = useCurrentUser();
  const create = useCreateEvent();
  const update = useUpdateEvent();
  const mutation = isEdit ? update : create;

  const [title,           setTitle]           = useState("");
  const [summary,         setSummary]         = useState("");
  const [description,     setDescription]     = useState("");
  const [eventType,       setEventType]       = useState<EventType>("conference");
  const [location,        setLocation]        = useState("");
  const [startAt,         setStartAt]         = useState("");
  const [endAt,           setEndAt]           = useState("");
  const [registrationUrl, setRegistrationUrl] = useState("");
  const [isPublished,     setIsPublished]     = useState(false);
  const [image,           setImage]           = useState<File | null>(null);
  const [progress,        setProgress]        = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setTitle(event.title);
      setSummary(event.summary || "");
      setDescription("");                                 // detail field; user can re-type
      setEventType(event.event_type);
      setLocation(event.location || "");
      setStartAt(toDatetimeLocal(event.start_at));
      setEndAt(toDatetimeLocal(event.end_at));
      setRegistrationUrl(event.registration_url || "");
      setIsPublished(event.is_published);
    } else {
      setTitle(""); setSummary(""); setDescription("");
      setEventType("conference"); setLocation("");
      setStartAt(defaultStartAt()); setEndAt("");
      setRegistrationUrl(""); setIsPublished(false);
    }
    setImage(null);
    setProgress(null);
    create.reset();
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.id]);

  const canSubmit = title.trim().length > 0
    && startAt.length > 0
    && !mutation.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const wantsProgress = !!image;
    if (wantsProgress) setProgress(0);
    const input: EventInput = {
      title:            title.trim(),
      summary:          summary.trim() || undefined,
      description:      description.trim() || undefined,
      event_type:       eventType,
      location:         location.trim() || undefined,
      start_at:         new Date(startAt).toISOString(),
      end_at:           endAt ? new Date(endAt).toISOString() : null,
      registration_url: registrationUrl.trim() || undefined,
      is_published:     isPublished,
      image:            image || undefined,
      onProgress:       wantsProgress ? setProgress : undefined,
    };
    const handlers = {
      onSuccess: () => onOpenChange(false),
      onSettled: () => setProgress(null),
    };
    if (isEdit && event) {
      update.mutate({ id: event.id, input }, handlers);
    } else {
      create.mutate(input, handlers);
    }
  }

  const err = mutation.error as
    | { response?: { data?: Record<string, unknown> | { detail?: string } } }
    | undefined;
  const errorMsg = formatApiError(err);

  const publishHint = isPublished
    ? user?.is_ceo_level
      ? "Goes live on the public site immediately."
      : "Director-published events go to the CEO queue for final sign-off."
    : "Saved as a draft only — switch this on when you're ready.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit “${event?.title}”` : "Host a new event"}</DialogTitle>
          <DialogDescription>
            Convening, workshop, or field engagement details. Cover image is optional.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ef-title">Title <span className="text-destructive">*</span></Label>
              <Input
                id="ef-title" required maxLength={255}
                value={title} onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. National Climate Adaptation Forum"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-type">Type</Label>
              <select
                id="ef-type"
                value={eventType} onChange={(e) => setEventType(e.target.value as EventType)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm font-medium"
              >
                {TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-location">Location</Label>
              <Input
                id="ef-location"
                value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="Addis Ababa, hotel name, virtual…"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-start">Starts <span className="text-destructive">*</span></Label>
              <Input
                id="ef-start" type="datetime-local" required
                value={startAt} onChange={(e) => setStartAt(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-end">Ends <span className="text-xs text-muted-foreground">— optional</span></Label>
              <Input
                id="ef-end" type="datetime-local"
                value={endAt} onChange={(e) => setEndAt(e.target.value)}
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ef-summary">Summary <span className="text-xs text-muted-foreground">— one-liner shown on the card</span></Label>
              <Input
                id="ef-summary" maxLength={300}
                value={summary} onChange={(e) => setSummary(e.target.value)}
                placeholder="Two-day forum bringing together regional climate leads."
              />
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="ef-desc">Description / agenda</Label>
              <Textarea
                id="ef-desc" rows={4}
                value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder={isEdit
                  ? "Leave blank to keep the existing description."
                  : "Full agenda, speakers, programme."}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-rsvp">Registration URL</Label>
              <Input
                id="ef-rsvp" type="url"
                value={registrationUrl} onChange={(e) => setRegistrationUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ef-image">Cover image <span className="text-xs text-muted-foreground">— optional</span></Label>
              <ImagePicker image={image} onChange={setImage} />
              <UploadProgressBar progress={progress} totalBytes={image?.size} />
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3">
            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-primary"
              />
              <div>
                <div className="font-medium">Publish to the public events page</div>
                <div className="text-xs text-muted-foreground">{publishHint}</div>
              </div>
            </label>
          </div>

          {errorMsg && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button" variant="ghost"
              onClick={() => onOpenChange(false)} disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending
                ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {progress !== null && progress >= 0 && progress < 100
                      ? `Uploading ${progress}%`
                      : "Saving…"}
                  </>
                )
                : isEdit
                  ? <><Save className="mr-2 h-4 w-4" />Save changes</>
                  : <><CalendarPlus className="mr-2 h-4 w-4" />Create event</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** "2026-05-23T15:30" from an ISO string, in the user's local TZ.
 *  datetime-local inputs are timezone-naive, so we drop the Z. */
function toDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultStartAt(): string {
  // Default: next hour, local time.
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return toDatetimeLocal(d.toISOString());
}

function ImagePicker({
  image, onChange,
}: { image: File | null; onChange: (f: File | null) => void }) {
  return image ? (
    <div className="flex items-center justify-between rounded-md border bg-muted/30 p-3 text-sm">
      <div className="min-w-0">
        <div className="truncate font-medium">{image.name}</div>
        <div className="text-xs text-muted-foreground">{formatBytes(image.size)}</div>
      </div>
      <Button type="button" variant="ghost" size="icon" onClick={() => onChange(null)}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  ) : (
    <label
      htmlFor="ef-image-input"
      className="flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/20 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted/40"
    >
      <Upload className="h-4 w-4" />
      <span>Choose image</span>
      <input
        id="ef-image-input" type="file" accept="image/*"
        className="sr-only"
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
      />
    </label>
  );
}

function formatApiError(
  err: { response?: { data?: Record<string, unknown> | { detail?: string } } } | undefined,
): string | null {
  if (!err?.response?.data) return null;
  const data = err.response.data as Record<string, unknown> & { detail?: string };
  if (typeof data.detail === "string") return data.detail;
  for (const [field, msgs] of Object.entries(data)) {
    if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === "string") {
      return `${field}: ${msgs[0]}`;
    }
  }
  return "Save failed.";
}
